import { ReactNode, forwardRef } from 'react';

import CloseButton from './CloseButton';

import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { getTextColor } from '~/utilities/colors';

type Props = TooltipTriggerProps & {
  backgroundColor?: string;
  borderColor?: string;
  /** Text can be rendered as a child instead of a prop */
  children?: ReactNode;
  'data-cy'?: string;
  icon?: ReactNode;
  onRemove?: () => void;
  text?: ReactNode;
  textColor?: string;
};

export default forwardRef<HTMLDivElement, Props>(function Chip(props, forwardedRef) {
  let bgClassName: string | undefined = 'bg-background-2';
  let textClassName: string | undefined = 'text-type-primary';
  if (props.backgroundColor) {
    bgClassName = undefined;
    textClassName = getTextColor(props.backgroundColor);
  }
  if (props.textColor) {
    textClassName = undefined;
  }

  const Icon = props.icon;

  return (
    <div
      {...pickTooltipTriggerProps(props)}
      ref={forwardedRef}
      className={[
        'flex h-6 w-min max-w-full shrink-0 cursor-default items-center gap-0.5 truncate whitespace-nowrap rounded px-1.5 type-label',
        bgClassName,
        textClassName,
      ].join(' ')}
      data-cy={props['data-cy']}
      style={{
        backgroundColor: props.backgroundColor,
        border: props.borderColor ? '1px solid' : 'none',
        borderColor: props.borderColor,
        color: props.textColor,
      }}
    >
      {Icon && (
        /*
          We're going to override the size of the icon that's been passed in.
          Currently, the icons passed in are almost always guaranteed to be MUI icons which
          set their own size via the font-size property. This allows us to override it.
          
          We could've also taken the icon component type as the prop and rendered it with a
          `fontSize="inherit"` prop but that'll require too many changes from users of this
          component if we ever start supporting non-MUI icons.
          */
        <div className="flex items-center [&_*]:!icon-sm">{Icon}</div>
      )}
      {props.text ? <div className="truncate">{props.text}</div> : null}
      {props.children ? <div className="truncate">{props.children}</div> : null}
      {props.onRemove && (
        <CloseButton
          backgroundColor={props.backgroundColor}
          data-cy={`remove-${props['data-cy']}`}
          onPress={props.onRemove}
        />
      )}
    </div>
  );
});
